import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { isProblem } from '@/lib/api/problem';

/**
 * The presentational atoms the §6 scheduling screens share, plus the two time helpers the
 * calendar needs.
 *
 * A deliberate copy of `features/inventory/ui.tsx` — BP-00 R5 makes a feature package a
 * boundary, and thirty repeated lines are cheaper than a shared `lib/ui` every BP has to
 * agree about.
 */
export function Async<T>({
  pending,
  error,
  data,
  children,
}: {
  pending: boolean;
  error: unknown;
  data: T | undefined;
  children: (value: T) => ReactNode;
}): JSX.Element {
  const { t } = useTranslation('common');

  if (pending) {
    return (
      <p role="status" data-testid="loading" className="text-muted-foreground">
        {t('state.loading')}
      </p>
    );
  }
  if (error !== null && error !== undefined) {
    return (
      <p role="alert" data-testid="load-error" className="rounded bg-status-no-show p-3 text-destructive">
        {isProblem(error) ? error.title : t('state.error')}
      </p>
    );
  }
  return <>{children(data as T)}</>;
}

/** A labelled dialog with a backdrop and a close action. */
export function Modal({
  title,
  testId,
  onClose,
  children,
}: {
  title: string;
  testId: string;
  onClose: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-10 flex items-start justify-center bg-foreground/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        className="max-h-full w-full max-w-2xl overflow-y-auto rounded bg-card p-4 shadow-lg"
      >
        <div className="mb-3 flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-2 py-1"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * `Modal`'s contents docked beside the page instead of over it (BP-36 rule 3): the same
 * title and close action, no backdrop, and the page beside it stays usable. A
 * complementary landmark rather than a dialog, because nothing is blocked while it is open.
 */
export function Panel({
  title,
  testId,
  onClose,
  children,
}: {
  title: string;
  testId: string;
  onClose: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <aside
      aria-label={title}
      data-testid={testId}
      className="@container sticky top-0 max-h-screen min-w-0 overflow-y-auto rounded-lg border border-border bg-card p-4"
    >
      <div className="mb-3 flex items-start justify-between gap-4">
        <h2 className="text-lg font-semibold">{title}</h2>
        <button type="button" onClick={onClose} className="rounded border border-border px-2 py-1">
          ×
        </button>
      </div>
      {children}
    </aside>
  );
}

/** Integer paise as rupees (ARCHITECTURE §3 Money: never a float on the wire). */
export function rupees(paise: number): string {
  return (paise / 100).toFixed(2);
}

/**
 * An instant as `HH:mm` **in the tenant's timezone**.
 *
 * The api sends UTC; a salon in Mumbai reads 10:30, not 05:00Z. `Intl` does the conversion,
 * so no date arithmetic here has to know about the offset (BP-04 R21).
 */
export function clockTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** A calendar date as `YYYY-MM-DD` in `timezone` — the form every §5 `date` query takes. */
export function tenantDate(timezone: string, at: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

/** `YYYY-MM-DD` shifted by whole days, without touching a timezone. */
export function shiftDate(date: string, days: number): string {
  const moved = new Date(`${date}T00:00:00Z`);
  moved.setUTCDate(moved.getUTCDate() + days);
  return moved.toISOString().slice(0, 10);
}
